import { useState } from "react"
import styled from "styled-components"

import { Button, spacing, Text } from "@minuthq/meatball-ui-react"

import { Pager } from "src/components/Pager/Pager"
import { useUrlPager } from "src/components/Pager/useUrlPager"
import { CreateTaskTemplateDialog } from "src/components/TaskManagement/templates/CreateTaskTemplateDialog"
import { DeleteTaskTemplateDialog } from "src/components/TaskManagement/templates/DeleteTaskTemplateDialog"
import { EditTaskTemplateDialog } from "src/components/TaskManagement/templates/EditTaskTemplateDialog"
import { TaskTemplateFilters } from "src/components/TaskManagement/templates/TaskTemplateFilters"
import { TaskTemplatesFilteredEmptyState } from "src/components/TaskManagement/templates/TaskTemplatesFilteredEmptyState"
import { usePostEditTaskTemplateInitiated } from "src/data/analytics/queries/taskManagementAnalyticsQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useTaskTypeLabels } from "src/data/taskManagement/hooks/useFetchTaskTableData"
import {
  TTaskTemplate,
  TTaskType,
} from "src/data/taskManagement/taskManagementTypes"
import { useFetchTaskTemplates } from "src/data/taskManagement/taskTemplateQueries"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { GridTable } from "src/ui/GridTable/GridTable"
import { MoreButton } from "src/ui/GridTable/MoreButton"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import { Titlebar } from "src/ui/Layout/Titlebar"
import { InternalLink } from "src/ui/Link/InternalLink"

export function TaskTemplates() {
  const { t, langKeys } = useTranslate()
  const { org } = useOrganization()

  const { limit, offset, setOffset } = useUrlPager({
    initialLimit: 15,
    initialOffset: 0,
  })

  const [openDialog, setOpenDialog] = useState(false)
  const [selectedTemplate, setSelectedTemplate] =
    useState<TTaskTemplate | null>(null)
  const [deleteTemplate, setDeleteTemplate] = useState<TTaskTemplate | null>(
    null
  )
  const [searchString, setSearchString] = useState<string>("")
  const [taskType, setTaskType] = useState<TTaskType | null>(null)

  const taskTypes = useTaskTypeLabels()

  const postEditTaskTemplateInitiated = usePostEditTaskTemplateInitiated()

  const fetchTaskTemplates = useFetchTaskTemplates({
    orgId: org.id,
    filters: {
      limit,
      offset,
      title: searchString,
      task_category: taskType ?? undefined,
    },
  })

  const taskTemplates = fetchTaskTemplates.data?.templates || []

  const isFiltered = !!searchString.trim() || !!taskType

  const hasNoTemplatesOnFilter =
    !taskTemplates.length && !fetchTaskTemplates.isLoading && isFiltered

  const columns: TableColumn<TTaskTemplate>[] = [
    {
      value: "title",
      label: t(langKeys.task_management_task_title),
      enableSort: true,
      sortButtonSpacing: "compact",
      disableClickPropagation: true,
      renderCell: (data) => {
        return (
          <InternalLink
            to={Routes.TaskTemplates.location()}
            onClick={() => setSelectedTemplate(data)}
          >
            {data.title}
          </InternalLink>
        )
      },
      columnWidth: "max-content",
    },
    {
      value: "type",
      label: t(langKeys.type),
      enableSort: true,
      sortButtonSpacing: "compact",
      renderCell: (data) => {
        return <Text variant="body">{taskTypes[data.task_category]}</Text>
      },
      columnWidth: "auto",
    },
    {
      value: "more_button",
      label: "",
      renderCell: (data) => (
        <MoreButtonWrapper>
          <MoreButton
            onDelete={() => setDeleteTemplate(data)}
            deleteLabel={t(langKeys.delete)}
            onEdit={() => {
              setSelectedTemplate(data)

              org.user_role &&
                postEditTaskTemplateInitiated.mutate({
                  task_type: data.task_category,
                  user_role_assignee: org.user_role,
                })
            }}
            editLabel={t(langKeys.task_management_edit_task_template)}
          />
        </MoreButtonWrapper>
      ),
    },
  ]

  const { rows, headerElements, templateColumns } =
    useTableColumns<TTaskTemplate>({
      columns,
      data: taskTemplates,
    })

  return (
    <div>
      <Titlebar
        title={t(langKeys.template_plural)}
        titleVariant="heading2"
        description={t(langKeys.task_management_templates_description)}
        actionBar={
          <Button onClick={() => setOpenDialog(true)} size="small">
            {t(langKeys.create_a_template)}
          </Button>
        }
        size="unset"
      />

      <FilterWrapper>
        <TaskTemplateFilters
          searchString={searchString}
          setSearchString={setSearchString}
          taskType={taskType}
          setTaskType={setTaskType}
        />
      </FilterWrapper>

      <GridTable
        header={headerElements}
        rows={rows}
        templateColumns={templateColumns}
        loading={fetchTaskTemplates.isLoading}
        useFallbackResponsiveMode
      />

      {hasNoTemplatesOnFilter && <TaskTemplatesFilteredEmptyState />}

      <Pager
        limit={limit}
        offset={offset}
        setOffset={setOffset}
        totalCount={fetchTaskTemplates.data?.paging.total_count}
      />

      {openDialog && (
        <CreateTaskTemplateDialog onClose={() => setOpenDialog(false)} />
      )}

      {selectedTemplate && (
        <EditTaskTemplateDialog
          template={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
        />
      )}

      {deleteTemplate && (
        <DeleteTaskTemplateDialog
          template={deleteTemplate}
          onClose={() => setDeleteTemplate(null)}
        />
      )}
    </div>
  )
}

const MoreButtonWrapper = styled.div`
  display: flex;
  place-items: end center;
`

const FilterWrapper = styled.div`
  margin-bottom: ${spacing.L};
`
